import { produce } from 'immer';
import { CATEGORY_TYPES } from 'js/hooks/games/types';

import * as Types from './types';
import * as helpers from './helpers';
import { TGamesFeedCategoryParsed } from '../types';
import { actionTypes, DEFAULT_FILTERS_STATE } from './constants';

export const defaultState: Types.IGamesGridState = {
    activeTab: '',
    isLoading: true,
    favoriteGames: [],
    feedName: undefined,
    gamesFeed: undefined,
    isFiltersChanged: false,
    isFiltersPopupOpened: false,
    ...DEFAULT_FILTERS_STATE
};

export const reducer = (reducerSuffix: string) =>
    produce((draftState, action: Types.TGamesGridAction): Types.IGamesGridState | void => {
        switch (action.type) {
            case actionTypes.GAMES_GRID_IS_LOADING_TOGGLE:
            case (actionTypes.GAMES_GRID_IS_LOADING_TOGGLE + reducerSuffix) as actionTypes.GAMES_GRID_IS_LOADING_TOGGLE: {
                Object.assign(draftState, {
                    isLoading: action.payload
                });

                break;
            }

            case actionTypes.GAMES_GRID_FILTERS_FILTER_POPUP_TOGGLE:
            case (actionTypes.GAMES_GRID_FILTERS_FILTER_POPUP_TOGGLE + reducerSuffix
            ) as actionTypes.GAMES_GRID_FILTERS_FILTER_POPUP_TOGGLE: {
                Object.assign(draftState, {
                    isFiltersPopupOpened: action.payload
                });

                break;
            }

            case actionTypes.GAMES_GRID_FEED_NAME_UPDATE:
            case (actionTypes.GAMES_GRID_FEED_NAME_UPDATE + reducerSuffix) as actionTypes.GAMES_GRID_FEED_NAME_UPDATE: {
                Object.assign(draftState, {
                    feedName: action.payload
                });

                break;
            }

            case actionTypes.GAMES_GRID_ACTIVE_TAB_UPDATE:
            case (actionTypes.GAMES_GRID_ACTIVE_TAB_UPDATE + reducerSuffix) as actionTypes.GAMES_GRID_ACTIVE_TAB_UPDATE: {
                Object.assign(draftState, {
                    activeTab: action.payload
                });

                break;
            }

            case actionTypes.GAMES_GRID_FEED_UPDATE:
            case (actionTypes.GAMES_GRID_FEED_UPDATE + reducerSuffix) as actionTypes.GAMES_GRID_FEED_UPDATE: {
                Object.assign(draftState, {
                    gamesFeed: action.payload
                });

                break;
            }

            case actionTypes.GAMES_GRID_FILTERS_SORT_BY_UPDATE:
            case (actionTypes.GAMES_GRID_FILTERS_SORT_BY_UPDATE + reducerSuffix
            ) as actionTypes.GAMES_GRID_FILTERS_SORT_BY_UPDATE: {
                draftState.filterSortBy = action.payload;
                draftState.isFiltersChanged = helpers.isFiltersChanged(draftState, DEFAULT_FILTERS_STATE);

                break;
            }

            case actionTypes.GAMES_GRID_FILTERS_PROVIDERS_UPDATE:
            case (
                actionTypes.GAMES_GRID_FILTERS_PROVIDERS_UPDATE + reducerSuffix
            ) as actionTypes.GAMES_GRID_FILTERS_PROVIDERS_UPDATE: {
                draftState.filterProviders = action.payload;
                draftState.isFiltersChanged = helpers.isFiltersChanged(draftState, DEFAULT_FILTERS_STATE);

                break;
            }

            case actionTypes.GAMES_GRID_FILTERS_LABELS_UPDATE:
            case (actionTypes.GAMES_GRID_FILTERS_LABELS_UPDATE + reducerSuffix) as actionTypes.GAMES_GRID_FILTERS_LABELS_UPDATE: {
                draftState.filterLabels = action.payload;
                draftState.isFiltersChanged = helpers.isFiltersChanged(draftState, DEFAULT_FILTERS_STATE);

                break;
            }

            case actionTypes.GAMES_GRID_FILTERS_CATEGORY_UPDATE:
            case (
                actionTypes.GAMES_GRID_FILTERS_CATEGORY_UPDATE + reducerSuffix
            ) as actionTypes.GAMES_GRID_FILTERS_CATEGORY_UPDATE: {
                draftState.filterCategory = action.payload;
                draftState.isFiltersChanged = helpers.isFiltersChanged(draftState, DEFAULT_FILTERS_STATE);

                break;
            }

            case actionTypes.GAMES_GRID_FILTERS_ALL_UPDATE:
            case (
                actionTypes.GAMES_GRID_FILTERS_ALL_UPDATE + reducerSuffix
            ) as actionTypes.GAMES_GRID_FILTERS_ALL_UPDATE: {
                Object.assign(draftState, {
                    ...action.payload
                });

                draftState.isFiltersChanged = helpers.isFiltersChanged(draftState, DEFAULT_FILTERS_STATE);

                break;
            }

            case actionTypes.GAMES_GRID_FILTERS_RESET:
            case (
                actionTypes.GAMES_GRID_FILTERS_RESET + reducerSuffix
            ) as actionTypes.GAMES_GRID_FILTERS_RESET: {
                Object.assign(draftState, {
                    filterLabels: '',
                    filterCategory: '',
                    filterProviders: '',
                    filterCustomFields: {},
                    filterSortBy: 'default',
                    isFiltersChanged: false
                });

                break;
            }

            case actionTypes.GAMES_GRID_FILTERS_CUSTOM_FIELD_UPDATE:
            case (
                actionTypes.GAMES_GRID_FILTERS_CUSTOM_FIELD_UPDATE + reducerSuffix
            ) as actionTypes.GAMES_GRID_FILTERS_CUSTOM_FIELD_UPDATE: {
                const { fieldName, value } = action.payload;

                if (!value) {
                    delete draftState.filterCustomFields[fieldName];
                } else {
                    draftState.filterCustomFields[fieldName] = value;
                }
                draftState.isFiltersChanged = helpers.isFiltersChanged(draftState, DEFAULT_FILTERS_STATE);

                break;
            }

            case actionTypes.GAMES_GRID_RECENTLY_PLAYED_CATEGORY_UPDATE:
            case (
                actionTypes.GAMES_GRID_RECENTLY_PLAYED_CATEGORY_UPDATE + reducerSuffix
            ) as actionTypes.GAMES_GRID_RECENTLY_PLAYED_CATEGORY_UPDATE: {
                if (draftState.gamesFeed) {
                    draftState.gamesFeed.gamesFeedCategories.forEach((category: TGamesFeedCategoryParsed) => {
                        if (category.categoryConfiguration.categoryType === CATEGORY_TYPES.recent) {
                            category.categoryConfiguration.categoryGames = action.payload as string[];
                        }
                    });
                }

                break;
            }

            case actionTypes.GAMES_GRID_FAVORITES_UPDATE:
            case (actionTypes.GAMES_GRID_FAVORITES_UPDATE + reducerSuffix) as actionTypes.GAMES_GRID_FAVORITES_UPDATE: {
                draftState.favoriteGames = action.payload;

                break;
            }

            default: {
                break;
            }
        }
    }, defaultState);
